import { useAuthStore } from '../../state/authStore';
import { useEndSession } from './useEndSession';
import { usePartnerStore } from '../../state/partnerStore';

export const useAccessHelper = () => {
  const { authConfig } = useAuthStore();
  const { partner } = usePartnerStore();
  const { user, authtoken, createdOn, ttl } = authConfig;
  // authConfig rehydrates from localStorage — before login / after logout
  // `company` is absent and every allowance read below crashes. Empty shape
  // makes all gated allowances resolve false instead.
  const company = authConfig.company ?? ({ feature_flags: {} } as typeof authConfig.company);
  const endSession = useEndSession();

  // Not a pure check: on every false return the user has already been signed out.
  const hasValidAuthToken = (declineActivity?: any) => {
    const now = new Date();
    const lastActive = localStorage.last_active;

    if (authConfig === undefined || authConfig === null || !authtoken) {
      endSession();
      return false;
    }

    if (!declineActivity && (!lastActive || now.valueOf() > lastActive)) {
      endSession();
      return false;
    }

    const expiresOn = new Date(createdOn + ttl);

    if (expiresOn === undefined || expiresOn === null || now >= expiresOn) {
      endSession();
      return false;
    }

    return true;
  };

  const isAdmin = () => {
    if (!hasValidAuthToken()) {
      return false;
    }
    if (!user) {
      return false;
    }

    return user.role === 'admin';
  };

  const isManager = () => {
    if (!hasValidAuthToken()) {
      return false;
    }
    if (!user) {
      return false;
    }

    return user.is_manager;
  };

  const isPartnerPrincipal = (): boolean =>
    hasValidAuthToken() && user?.partner_role === 'principal';

  const isGrader = () => {
    if (!hasValidAuthToken()) {
      return false;
    }
    if (!user) {
      return false;
    }
    return user.is_grader;
  };

  const isCollaborator = () => hasValidAuthToken() && user?.is_collaborator;

  const isCreator = () => hasValidAuthToken() && user?.role === 'creator';

  const pathCheck = (accessPath: any) => {
    if (!accessPath || !accessPath.length) {
      return true;
    }

    const profile = (user.access_profile && user.access_profile.permissions) || {};

    const check = accessPath.reduce((acc: any, value: any) => {
      if (!acc) {
        return acc;
      }

      if (acc && acc[value]) {
        return acc[value];
      }

      return false;
    }, profile);

    return check === true;
  };

  const roleCheck = (roles: any) => {
    if (!roles || !roles.length) {
      return true;
    }

    const { role } = user;

    for (let i = 0; i < roles.length; i += 1) {
      if (roles[i] === 'collaborator' && isCollaborator()) {
        return true;
      }

      if (roles[i] === 'manager' && isManager()) {
        return true;
      }

      if (roles[i] === 'principal' && isPartnerPrincipal()) {
        return true;
      }

      if (roles[i] === role) {
        return true;
      }
    }

    return false;
  };

  const combinedCheck = (accessPath: any, multiPath: any, roles: any) => {
    // check role
    if (!roleCheck(roles)) {
      return false;
    }

    // if no profile allow based on role check
    if (!user.access_profile) {
      return true;
    }

    // check initial path
    if (pathCheck(accessPath)) {
      return true;
    }

    // check alternate path
    if (multiPath && multiPath.length) {
      for (let i = 0; i < multiPath.length; i += 1) {
        if (pathCheck(multiPath[i])) {
          return true;
        }
      }
    }

    return false;
  };

  const allowGeneralActivity = (type: any, roles: any) => {
    const rolesToCheck = roles || ['admin'];

    const types = [
      'users',
      'admin',
      'content',
      'createContent',
      'workshops',
      'assessments',
      'assignments',
      'journeys',
      'third-party',
    ];

    const checks: any = {
      users: [
        ['admin', 'users', 'view'],
        ['admin', 'users', 'add'],
      ],
      connect: [
        ['admin', 'connect', 'categories'],
        ['admin', 'connect', 'locations'],
        ['admin', 'connect', 'departments'],
        ['admin', 'connect', 'teams'],
        ['admin', 'connect', 'levels'],
        ['admin', 'connect', 'grades'],
        ['admin', 'connect', 'groups'],
        ['admin', 'connect', 'skills'],
      ],
      content: [
        ['admin', 'content', 'all_create'],
        ['admin', 'content', 'articles_create'],
        ['admin', 'content', 'files_create'],
        ['admin', 'content', 'videos_create'],
        ['admin', 'content', 'tracks_create'],
        ['admin', 'content', 'scorm_create'],
        ['admin', 'content', 'imported_content_create'],
        ['admin', 'content', 'all_edit'],
        ['admin', 'content', 'articles_edit'],
        ['admin', 'content', 'files_edit'],
        ['admin', 'content', 'videos_edit'],
        ['admin', 'content', 'tracks_edit'],
        ['admin', 'content', 'scorm_edit'],
        ['admin', 'content', 'imported_content_edit'],
        ['admin', 'content', 'all_analytics'],
        ['admin', 'content', 'articles_analytics'],
        ['admin', 'content', 'files_analytics'],
        ['admin', 'content', 'videos_analytics'],
        ['admin', 'content', 'tracks_analytics'],
        ['admin', 'content', 'scorm_analytics'],
        ['admin', 'content', 'imported_content_analytics'],
      ],
      createContent: [
        ['admin', 'content', 'all_create'],
        ['admin', 'content', 'articles_create'],
        ['admin', 'content', 'files_create'],
        ['admin', 'content', 'videos_create'],
        ['admin', 'content', 'tracks_create'],
        ['admin', 'content', 'scorm_create'],
        ['admin', 'content', 'imported_content_create'],
      ],
      workshops: [
        ['admin', 'content', 'all_create'],
        ['admin', 'content', 'workshops_create'],
        ['admin', 'content', 'all_edit'],
        ['admin', 'content', 'workshops_edit'],
        ['admin', 'content', 'workshops_analytics'],
      ],
      assessments: [
        ['admin', 'content', 'all_create'],
        ['admin', 'content', 'assessments_create'],
        ['admin', 'content', 'all_edit'],
        ['admin', 'content', 'assessments_edit'],
        ['admin', 'content', 'all_analytics'],
        ['admin', 'content', 'assessments_analytics'],
      ],
      assignments: [
        ['admin', 'assignments', 'view'],
        ['admin', 'assignments', 'edit'],
        ['admin', 'assignments', 'complete'],
        ['admin', 'assignments', 'create'],
        ['admin', 'assignments', 'archive'],
      ],
      journeys: [
        ['admin', 'journeys', 'view'],
        ['admin', 'journeys', 'edit'],
        ['admin', 'journeys', 'create'],
        ['admin', 'journeys', 'archive'],
      ],
    };

    if (types.indexOf(type) === -1) {
      return false;
    }

    for (let i = 0; i < checks[type].length; i += 1) {
      if (combinedCheck(checks[type][i], null, rolesToCheck)) {
        return true;
      }
    }

    return false;
  };

  const isAllowed = (accessPath: any, multiPath: any, roles: any) => {
    // If profile hasn't been checked or no user - deny access and signout
    if (!user || !user._id) {
      endSession();
      return false;
    }

    return combinedCheck(accessPath, multiPath, roles);
  };

  // Shared by `assessments` and `assessmentsMigration` — the migration
  // allowance must never exceed the base assessments allowance.
  const assessmentsAllowed =
    company.assessments &&
    allowGeneralActivity('assessments', ['admin', 'creator', 'collaborator']);

  const allowance = {
    continuInsights:
      company.feature_flags.enable_continu_insights &&
      isAllowed(['admin', 'reports'], null, ['admin']),
    overview: isAllowed(['admin', 'overview'], null, ['admin']),
    users: allowGeneralActivity('users', ['admin']),
    content: allowGeneralActivity('content', ['admin', 'creator', 'collaborator']),
    workshops:
      company.workshops && allowGeneralActivity('workshops', ['admin', 'creator', 'collaborator']),
    assessments: assessmentsAllowed,
    // Admin assessments React fold-in. Flag alone isn't enough — the base
    // assessments allowance (company toggle + per-type perms) still gates
    // who can touch the admin assessments surface at all.
    assessmentsMigration: !!(
      company.feature_flags.enable_assessments_migration && assessmentsAllowed
    ),
    assignments: allowGeneralActivity('assignments', ['admin', 'creator']),
    assignmentsCreate: isAllowed(['admin', 'assignments', 'create'], null, [
      'admin',
      'creator',
      'manager',
    ]),
    assignmentsV2: company.feature_flags.enable_assignment_view_v2,
    workflows: isAllowed(['admin', 'workflows'], null, ['admin']),
    reports: company.reports && isAllowed(['admin', 'reports'], null, ['admin']),
    registrationForms:
      company.feature_flags.enable_registration_forms && isAllowed(['admin'], null, ['admin']),
    badges: company.feature_flags.enable_badges && isAllowed(['admin', 'badges'], null, ['admin']),
    certificates:
      company.feature_flags.enable_standalone_certificates &&
      isAllowed(['admin', 'certificates'], null, ['admin']),
    // permissions.admin.academies is a group (manage today; publish/edit/analytics
    // may split out later), so the path goes one level deeper than its siblings.
    academies:
      company.feature_flags.enable_academies &&
      isAllowed(['admin', 'academies', 'manage'], null, ['admin']),
    journeys:
      company.feature_flags.journeys && allowGeneralActivity('journeys', ['admin', 'creator']),
    surveys:
      company.feature_flags.enable_surveys && isAllowed(['admin', 'surveys'], null, ['admin']),
    settings: isAllowed(['admin', 'settings'], null, ['admin']),
    apiApplications:
      company.linkedin_learning || company.feature_flags.enable_masterclass || company.open_api,
    profiles: company.profiles,
    partners: company.feature_flags.enable_partners && isAllowed(['admin'], null, ['admin']),
    partnerManager: company.feature_flags.enable_partners && !!partner._id && isPartnerPrincipal(),
    inPartner: company.feature_flags.enable_partners && !!partner._id,
    webhooks: company.feature_flags.enable_webhooks,
    theme: isAllowed(['explore', 'update_theme'], null, ['admin']),
    recommendations: company.feature_flags.enable_recommendations,
    metadata: company.feature_flags.enable_metadata,
    ecommerce: company.feature_flags.enable_ecommerce,
    // Flag + role only — deliberately NO access-profile path: scoped admins
    // can be hub admins (the API's adminOnly middleware checks role alone,
    // and roster membership is the real authorization). Hub-admin membership
    // lives on the API-side Hub doc — useIsHubAdmin probes it; gate on both.
    hubs: company.feature_flags.enable_hubs && isAllowed([], null, ['admin']),
    connectCategories: isAllowed(['admin', 'connect', 'categories'], null, ['admin']),
    connectLocations: isAllowed(['admin', 'connect', 'locations'], null, ['admin']),
    connectDepartments: isAllowed(['admin', 'connect', 'departments'], null, ['admin']),
    connectTeams: isAllowed(['admin', 'connect', 'teams'], null, ['admin']),
    connectLevels: isAllowed(['admin', 'connect', 'levels'], null, ['admin']),
    connectGrades: isAllowed(['admin', 'connect', 'grades'], null, ['admin']),
    connectGroups: company.groups && isAllowed(['admin', 'connect', 'groups'], null, ['admin']),
    connectSkills: isAllowed(['admin', 'connect', 'skills'], null, ['admin']),
  };

  return {
    isAdmin,
    isManager,
    isGrader,
    isCreator,
    allowGeneralActivity,
    isAllowed,
    isPartnerPrincipal,
    allowance,
  };
};
